/**
 * Curved Marquee Product Carousel
 * Infinite RAF marquee + barrel/cylinder card transforms + touch drag
 */
(function () {
  const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const HOVER = window.matchMedia('(hover: hover) and (pointer: fine)');

  function clamp(n, min, max) {
    return Math.max(min, Math.min(max, n));
  }

  function init(root) {
    if (!root || root.__dcmInit) return;

    const viewport = root.querySelector('[data-dcm-viewport]');
    const track = root.querySelector('[data-dcm-track]');
    const set = root.querySelector('[data-dcm-set]');
    if (!viewport || !track || !set) return;
    if (set.querySelectorAll('[data-dcm-card]').length < 1) return;

    root.__dcmInit = true;

    const loopSeconds = Math.max(8, parseFloat(root.getAttribute('data-loop-seconds') || '30') || 30);
    const curve = clamp(parseFloat(root.getAttribute('data-curve') || '55') || 55, 0, 100) / 100;
    const pauseHover = root.getAttribute('data-pause-hover') !== 'false';

    const state = {
      offset: 0,
      setWidth: 0,
      paused: false,
      hoverPaused: false,
      dragging: false,
      pointerId: null,
      startX: 0,
      startOffset: 0,
      velocity: 0,
      lastX: 0,
      lastT: 0,
      inView: true,
      raf: 0,
      lastFrame: 0,
      pxPerMs: 0,
      cards: [],
    };

    const measure = () => {
      state.setWidth = set.getBoundingClientRect().width;
      state.pxPerMs = state.setWidth > 0 ? state.setWidth / (loopSeconds * 1000) : 0;
      state.cards = Array.from(track.querySelectorAll('[data-dcm-card]'));
      applyCurve();
    };

    const wrap = () => {
      if (state.setWidth <= 0) return;
      while (state.offset >= state.setWidth) state.offset -= state.setWidth;
      while (state.offset < 0) state.offset += state.setWidth;
    };

    const applyCurve = () => {
      if (!state.cards.length) return;

      const vr = viewport.getBoundingClientRect();
      const centerX = vr.left + vr.width / 2;
      const half = Math.max(vr.width * 0.5, 1);
      const maxRot = 42 * curve;
      const maxZ = 120 * curve;
      const maxY = 28 * curve;
      const scaleAmp = 0.22 * curve;

      for (let i = 0; i < state.cards.length; i++) {
        const card = state.cards[i];
        const cr = card.getBoundingClientRect();
        const cardCenter = cr.left + cr.width / 2;
        const norm = clamp((cardCenter - centerX) / half, -1.4, 1.4);
        const abs = Math.abs(norm);
        const ease = 1 - Math.pow(1 - clamp(1 - abs / 1.15, 0, 1), 1.6);

        const rotY = -norm * maxRot;
        const translateZ = (ease - 0.35) * maxZ;
        const translateY = (1 - ease) * maxY;
        const scale = 1 - abs * scaleAmp * 0.55 + ease * scaleAmp * 0.35;
        const opacity = clamp(1 - abs * 0.28 * curve, 0.42, 1);
        const zIndex = Math.round(100 - abs * 40);

        card.style.zIndex = String(zIndex);
        card.style.opacity = opacity.toFixed(3);
        card.style.webkitTransform =
          'translate3d(0,' +
          translateY.toFixed(2) +
          'px,' +
          translateZ.toFixed(2) +
          'px) rotateY(' +
          rotY.toFixed(2) +
          'deg) scale(' +
          scale.toFixed(4) +
          ')';
        card.style.transform = card.style.webkitTransform;
      }
    };

    const paint = () => {
      wrap();
      track.style.webkitTransform = 'translate3d(' + -state.offset.toFixed(2) + 'px,0,0)';
      track.style.transform = track.style.webkitTransform;
      applyCurve();
    };

    const tick = (now) => {
      const t = now || performance.now();
      if (!state.lastFrame) state.lastFrame = t;
      const dt = Math.min(48, t - state.lastFrame);
      state.lastFrame = t;

      const auto =
        !REDUCE &&
        !state.paused &&
        !state.hoverPaused &&
        !state.dragging &&
        state.inView &&
        !document.hidden;

      if (auto && state.pxPerMs > 0) {
        state.offset += state.pxPerMs * dt;
      } else if (!state.dragging && Math.abs(state.velocity) > 0.02) {
        state.offset += state.velocity * dt;
        state.velocity *= 0.95;
        if (Math.abs(state.velocity) < 0.02) state.velocity = 0;
      }

      paint();
      state.raf = requestAnimationFrame(tick);
    };

    // Images may shift set width
    const imgs = Array.from(root.querySelectorAll('img'));
    let pending = imgs.filter((img) => !img.complete).length;
    const onImg = () => {
      pending -= 1;
      measure();
    };
    imgs.forEach((img) => {
      if (img.complete) return;
      img.addEventListener('load', onImg, { once: true });
      img.addEventListener('error', onImg, { once: true });
    });

    measure();

    let resizeTimer = 0;
    window.addEventListener(
      'resize',
      () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(measure, 90);
      },
      { passive: true }
    );

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          state.inView = entries.some((en) => en.isIntersecting && en.intersectionRatio > 0.05);
        },
        { threshold: [0, 0.05, 0.2] }
      );
      io.observe(viewport);
    }

    if (HOVER.matches && pauseHover) {
      viewport.addEventListener('pointerenter', () => {
        if (!state.dragging) state.hoverPaused = true;
      });
      viewport.addEventListener('pointerleave', () => {
        state.hoverPaused = false;
      });
    }

    const down = (e) => {
      if (e.button != null && e.button !== 0) return;
      state.dragging = true;
      state.velocity = 0;
      state.pointerId = e.pointerId;
      state.startX = e.clientX;
      state.startOffset = state.offset;
      state.lastX = e.clientX;
      state.lastT = performance.now();
      viewport.classList.add('is-dragging');
      try {
        viewport.setPointerCapture(e.pointerId);
      } catch (_) {}
    };

    const move = (e) => {
      if (!state.dragging) return;
      if (state.pointerId != null && e.pointerId !== state.pointerId) return;

      const dx = e.clientX - state.startX;
      state.offset = state.startOffset - dx;

      const now = performance.now();
      const dt = Math.max(1, now - state.lastT);
      const vx = (e.clientX - state.lastX) / dt;
      state.velocity = -vx;
      state.lastX = e.clientX;
      state.lastT = now;

      if (e.cancelable && Math.abs(dx) > 4) e.preventDefault();
    };

    const up = (e) => {
      if (!state.dragging) return;
      if (state.pointerId != null && e.pointerId !== state.pointerId) return;
      state.dragging = false;
      state.pointerId = null;
      viewport.classList.remove('is-dragging');
      // Cap fling so it feels natural
      state.velocity = clamp(state.velocity, -1.8, 1.8);
    };

    viewport.addEventListener('pointerdown', down);
    viewport.addEventListener('pointermove', move, { passive: false });
    viewport.addEventListener('pointerup', up);
    viewport.addEventListener('pointercancel', up);

    // Prevent accidental click after drag
    let dragMoved = false;
    viewport.addEventListener('pointerdown', () => {
      dragMoved = false;
    });
    viewport.addEventListener('pointermove', (e) => {
      if (state.dragging && Math.abs(e.clientX - state.startX) > 8) dragMoved = true;
    });
    viewport.addEventListener(
      'click',
      (e) => {
        if (dragMoved) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      true
    );

    if (REDUCE) {
      // Static curve only — native horizontal scroll via CSS overflow
      applyCurve();
      return;
    }

    state.raf = requestAnimationFrame(tick);
  }

  function scan() {
    document.querySelectorAll('[data-drip-cm]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
  window.addEventListener('load', scan);

  document.addEventListener('shopify:section:load', (e) => {
    const el = e.target?.querySelector?.('[data-drip-cm]') || e.target;
    if (el?.matches?.('[data-drip-cm]')) {
      el.__dcmInit = false;
      init(el);
    } else {
      scan();
    }
  });
})();
